import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {IntersectionObserver | null} */
  #purchaseZoneIntersectionObserver = null;

  /** @type {Map<Element, boolean>} */
  #purchaseZoneVisibility = new Map();

  /** @type {number | null} */
  #purchaseZoneFallbackFrame = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#abortController || this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#purchaseZoneIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;
    this.#mainBottomObserver = null;
    this.#purchaseZoneIntersectionObserver = null;
    this.#purchaseZoneVisibility.clear();
    if (this.#purchaseZoneFallbackFrame !== null) {
      cancelAnimationFrame(this.#purchaseZoneFallbackFrame);
      this.#purchaseZoneFallbackFrame = null;
    }
    this.#targetAddToCartButton = null;
    this.#hiddenByBottom = false;
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';

    const purchaseZoneGuardReady = this.#setupIntersectionObserver();
    if (!purchaseZoneGuardReady) {
      document.documentElement.classList.remove('cgau-product-purchase-zone-visible');
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });

    // CamGuard 25.09.2026: hide the bar while the chat panel is open, bring it back when it closes.
    const chat = document.querySelector('shopify-chat');
    if (chat && 'MutationObserver' in window) {
      const chatObserver = new MutationObserver(() => {
        if (chat.hasAttribute('open')) {
          if (this.#isStuck) this.#hideStickyBar();
        } else if (!this.#isStuck && !this.#hiddenByBottom && this.#buyButtonsScrolledPast()) {
          this.#showStickyBar();
        }
      });
      chatObserver.observe(chat, { attributes: true, attributeFilter: ['open'] });
      signal.addEventListener('abort', () => chatObserver.disconnect());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.documentElement.classList.remove('cgau-sticky-atc-visible');
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#purchaseZoneIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;
    this.#mainBottomObserver = null;
    this.#purchaseZoneIntersectionObserver = null;
    this.#purchaseZoneVisibility.clear();
    if (this.#purchaseZoneFallbackFrame !== null) {
      cancelAnimationFrame(this.#purchaseZoneFallbackFrame);
      this.#purchaseZoneFallbackFrame = null;
    }
    this.#targetAddToCartButton = null;
    this.#hiddenByBottom = false;
    this.#isStuck = false;
    document.documentElement.classList.remove('cgau-product-purchase-zone-visible');
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
      this.#resetTimeout = undefined;
    }
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
      this.#animationTimeout = null;
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility.
   *
   * @returns {boolean} Whether the purchase-zone guard was established.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return false;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return false;

    const purchaseZoneGuardReady = this.#setupPurchaseZoneGuard(buyButtonsBlock);
    if (!purchaseZoneGuardReady) return false;
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    // The purchase guard keeps its synchronous geometry state when observers
    // are unavailable. Do not continue into the sticky-bar observers below.
    if (!('IntersectionObserver' in window)) return true;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return true;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    return true;
  }

  /**
   * Keeps fixed trust/chat launchers out of the ordinary mobile purchase zone.
   * The Google launcher briefly owns a transparent 400px-wide iframe while it
   * expands, so z-index or fixed offsets cannot make the underlying controls
   * reliably clickable at every phone height.
   *
   * @param {Element} buyButtonsBlock
   * @returns {boolean} Whether at least one purchase-zone target was found.
   */
  #setupPurchaseZoneGuard(buyButtonsBlock) {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    const productPrice = productId
      ? section?.querySelector(`product-price[data-product-id="${CSS.escape(productId)}"]`)
      : section?.querySelector('product-price');
    const targets = [productPrice, buyButtonsBlock].filter((target) => target instanceof Element);
    const root = document.documentElement;

    if (!targets.length) return false;

    const syncRootState = () => {
      root.classList.toggle(
        'cgau-product-purchase-zone-visible',
        targets.some((target) => this.#purchaseZoneVisibility.get(target) === true)
      );
    };

    // Set the correct state synchronously after parse. Liquid applies the
    // protective class before first paint, so a late app iframe cannot cover
    // the buy box while this module is still loading.
    const measureTargetVisibility = () => targets.forEach((target) => {
      const bounds = target.getBoundingClientRect();
      this.#purchaseZoneVisibility.set(
        target,
        bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth
      );
    });
    measureTargetVisibility();
    syncRootState();

    if (!('IntersectionObserver' in window)) {
      // Old embedded browsers still get a conversion-safe guard. Throttle the
      // two geometry reads to one animation frame and release the app launchers
      // as soon as the purchase controls leave view.
      const scheduleGeometrySync = () => {
        if (this.#purchaseZoneFallbackFrame !== null) return;
        this.#purchaseZoneFallbackFrame = requestAnimationFrame(() => {
          this.#purchaseZoneFallbackFrame = null;
          measureTargetVisibility();
          syncRootState();
        });
      };
      const signal = this.#abortController?.signal;
      window.addEventListener('scroll', scheduleGeometrySync, { passive: true, signal });
      window.addEventListener('resize', scheduleGeometrySync, { passive: true, signal });
      return true;
    }

    this.#purchaseZoneIntersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => this.#purchaseZoneVisibility.set(entry.target, entry.isIntersecting));
      syncRootState();
    });
    targets.forEach((target) => this.#purchaseZoneIntersectionObserver?.observe(target));
    return true;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    document.documentElement.classList.add('cgau-sticky-atc-visible');
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    document.documentElement.classList.remove('cgau-sticky-atc-visible');
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    // CamGuard 25.09.2026: the Inbox launcher sits on every page, so "chat exists" kept
    // the bar hidden for good. Only an OPEN chat panel blocks the bar now; the closed
    // launcher steps aside instead (html.cgau-sticky-atc-visible in cgau-shell-v2.css).
    return Boolean(document.querySelector('shopify-chat[open]'));
  }

  /**
   * CamGuard 25.09.2026: whether the main buy buttons are above the viewport.
   * @returns {boolean}
   */
  #buyButtonsScrolledPast() {
    const block = this.#getProductForm()?.closest('.buy-buttons-block');
    if (!block) return false;
    return block.getBoundingClientRect().bottom < 0;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

// Product templates start in a conversion-safe state before this low-priority
// module loads. If a merchant disables the sticky component (or a custom
// product template omits it), release that temporary state after the document
// has parsed so the trust and chat launchers are not hidden forever.
if (!document.querySelector('sticky-add-to-cart')) {
  document.documentElement.classList.remove('cgau-product-purchase-zone-visible');
}
